<app-relatorio-container
    tituloRelatorio="Etiqueta Profissional"
    descricaoRelatorio="Folha A4 de etiquetas (carteira, profissional ou diploma) para vários médicos"
    categoriaRelatorio="Cadastro"
    [filtrosTemplate]="filtrosTemplate"
    [pdfBlob]="(job$ | async)?.pdfBlob"
    [pdfFileName]="(job$ | async)?.fileName ?? 'etiqueta-profissional.pdf'"
    [statusPreview]="(job$ | async)?.status ?? null"
    [progressoPreview]="(job$ | async)?.progress ?? 0"
>
    <ng-template #filtrosTemplate>
        <div class="flex flex-col gap-6" [formGroup]="form">
            <p class="text-sm text-color-secondary m-0">Escolha o tipo de documento, a UF do órgão e marque as posições da folha. Para cada posição informe o CRM e a UF do médico — o restante dos dados é resolvido automaticamente.</p>

            <div class="field">
                <label for="tipoDocumento" class="font-semibold block mb-2">Tipo de documento</label>
                <p-select inputId="tipoDocumento" formControlName="tipoDocumento" [options]="tipos" optionLabel="label" optionValue="value" appendTo="body" class="w-full" styleClass="w-full"></p-select>
            </div>

            <div class="field">
                <label class="font-semibold block mb-2">UF do órgão</label>
                <app-estado-select [uf]="ufOrgao" (ufChange)="onUfOrgaoChange($event)" [removeBR]="true"></app-estado-select>
                @if (ufOrgaoTocada && (!ufOrgao || ufOrgao === 'BR')) {
                    <small class="p-error mt-2" style="color: red">Selecione a UF do órgão.</small>
                }
            </div>

            <div class="field">
                <label class="font-semibold block mb-2">Posições da folha</label>
                <p class="text-xs text-color-secondary mt-0 mb-3">Clique numa célula (na folha ou na lista) para ativar/desativar e preencher os dados. As células preenchidas indicam as posições que serão impressas.</p>

                <!-- Mapa visual da folha A4: cada célula representa uma etiqueta; fica na cor primária quando a posição está selecionada. -->
                <div class="flex justify-center mb-4">
                    <div class="relative border-2 rounded" style="width: 150px; aspect-ratio: 210 / 297; background: var(--p-content-background); border-color: var(--p-content-border-color)" aria-label="Folha A4 — posições das etiquetas">
                        @for (grupo of posicoes.controls; track $index) {
                            <div
                                class="border rounded-sm flex items-center justify-center cursor-pointer text-xs font-semibold select-none transition-colors"
                                [ngStyle]="estiloEtiqueta(grupo)"
                                (click)="togglePosicao(grupo)"
                                title="Posição {{ grupo.get('posicao')?.value }}"
                            >
                                {{ grupo.get('posicao')?.value }}
                            </div>
                        }
                    </div>
                </div>

                <div class="grid grid-cols-1 gap-3" formArrayName="posicoes">
                    @for (grupo of posicoes.controls; track $index) {
                        <div
                            [formGroupName]="$index"
                            class="border rounded-lg p-3 transition-colors"
                            [class.border-primary]="grupo.get('ativo')?.value"
                            [ngStyle]="grupo.get('ativo')?.value ? { 'background-color': 'color-mix(in oklab, var(--p-primary-color) 12%, transparent)' } : {}"
                        >
                            <div class="flex items-center justify-between cursor-pointer" (click)="togglePosicao(grupo)">
                                <span class="font-semibold text-sm">Posição {{ grupo.get('posicao')?.value }}</span>
                                <p-checkbox [binary]="true" formControlName="ativo" inputId="ativo-{{ $index }}" [ariaLabel]="'Ativar posição ' + grupo.get('posicao')?.value" (click)="$event.stopPropagation()"></p-checkbox>
                            </div>

                            @if (grupo.get('ativo')?.value) {
                                <div class="flex flex-col gap-3 mt-3">
                                    <div class="flex gap-2">
                                        <div class="flex-1">
                                            <label class="text-xs block mb-1">CRM</label>
                                            <input type="text" pInputText formControlName="crm" class="w-full" placeholder="CRM" />
                                        </div>
                                        <div class="w-28">
                                            <label class="text-xs block mb-1">UF</label>
                                            <app-estado-select [uf]="grupo.get('uf')?.value ?? ''" (ufChange)="grupo.get('uf')?.setValue($event)" [removeBR]="true"></app-estado-select>
                                        </div>
                                    </div>

                                    <div class="flex gap-2 items-end">
                                        <div class="flex-1">
                                            <label class="text-xs block mb-1">Nº de controle (opcional)</label>
                                            <input type="text" pInputText formControlName="numeroControle" class="w-full" placeholder="Nº da etiqueta física" />
                                        </div>
                                        <div class="flex items-center gap-2 pb-2">
                                            <p-checkbox [binary]="true" inputId="reg-{{ $index }}" formControlName="registrar"></p-checkbox>
                                            <label for="reg-{{ $index }}" class="text-xs">Registrar</label>
                                        </div>
                                    </div>

                                    <div class="flex gap-2 items-end">
                                        <div class="flex-1">
                                            <label class="text-xs block mb-1">Data (opcional)</label>
                                            <input type="text" pInputText formControlName="dataEmissao" class="w-full" placeholder="dd/mm/aaaa" />
                                        </div>
                                        <div class="w-36">
                                            <label class="text-xs block mb-1 whitespace-nowrap">UF origem (opcional)</label>
                                            <input type="text" pInputText formControlName="ufOrigem" maxlength="2" class="w-full uppercase" placeholder="UF" />
                                        </div>
                                    </div>

                                    <div>
                                        <label class="text-xs block mb-1">Observação (opcional)</label>
                                        <input type="text" pInputText formControlName="observacao" class="w-full" placeholder="Ex.: dias do visto, documento/processo" />
                                    </div>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>

            <div class="field flex items-center gap-2">
                <p-checkbox [binary]="true" inputId="incluirAssinatura" formControlName="incluirAssinatura"></p-checkbox>
                <label for="incluirAssinatura" class="font-semibold">Incluir imagem de assinatura</label>
            </div>

            @if (mostrarAssinante1) {
                <div class="field">
                    <label for="assinante1" class="font-semibold block mb-2">Assinante 1</label>
                    <p-select
                        inputId="assinante1"
                        formControlName="assinante1"
                        [options]="assinantes()"
                        [loading]="loadingAssinantes()"
                        optionLabel="nome"
                        appendTo="body"
                        placeholder="Selecione o assinante"
                        [showClear]="true"
                        class="w-full"
                        styleClass="w-full"
                    >
                        <ng-template let-a pTemplate="item">{{ rotuloAssinante(a) }}</ng-template>
                        <ng-template let-a pTemplate="selectedItem">{{ a ? rotuloAssinante(a) : '' }}</ng-template>
                    </p-select>
                </div>
            }

            @if (mostrarAssinante2) {
                <div class="field">
                    <label for="assinante2" class="font-semibold block mb-2">Assinante 2</label>
                    <p-select
                        inputId="assinante2"
                        formControlName="assinante2"
                        [options]="assinantes()"
                        [loading]="loadingAssinantes()"
                        optionLabel="nome"
                        appendTo="body"
                        placeholder="Selecione o assinante"
                        [showClear]="true"
                        class="w-full"
                        styleClass="w-full"
                    >
                        <ng-template let-a pTemplate="item">{{ rotuloAssinante(a) }}</ng-template>
                        <ng-template let-a pTemplate="selectedItem">{{ a ? rotuloAssinante(a) : '' }}</ng-template>
                    </p-select>
                </div>
            }

            <div class="field pt-5 flex justify-end gap-2">
                <p-button label="Limpar" icon="pi pi-eraser" [variant]="'text'" (click)="limparFormulario()"></p-button>
                <p-button label="Gerar Etiquetas" icon="pi pi-file-pdf" [appCan]="roleGerar" (click)="gerarRelatorio()" [loading]="isPending" [disabled]="isPending"></p-button>
            </div>
        </div>
    </ng-template>
</app-relatorio-container>
